import type { AeaExerciseModel, GetBandInfoResponse, TeacherTestDetails } from "p6m-p6u";
import { ClassActivityCategory } from "../constants/ClassActivityConstants";
import { getAppBaseUrl } from "../utils/urlUtils";
import { getTestBook, getTrainingBook, type ActivityBook } from "./BookAvailability";
import type {
    ClassActivity,
    VocabTrainingActivity,
    TestActivity,
} from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import type { DuplicateVocabTrainingPopupState, OpenShareFlowParams } from "../hooks/useVocabTrainingOperations";

export const getStudentTestUrl = (testId: string): string => `${getAppBaseUrl()}/student/${testId}`;

export const getVocabCount = (content: { selectedWords?: unknown[] }[] | undefined): number => {
    if (!content) return 0;
    return content.reduce((total, exercise) => total + (exercise.selectedWords?.length ?? 0), 0);
};

export const hasTestVocabulary = (test: TeacherTestDetails): boolean => getVocabCount(test.content) > 0;

export const hasTrainingCards = (training: AeaExerciseModel): boolean => (training.cardIds?.length ?? 0) > 0;

export const getActivityBook = (activity: ClassActivity, userId: string): ActivityBook | undefined =>
    activity.category === ClassActivityCategory.TEST
        ? getTestBook(activity.testData)
        : getTrainingBook(activity.vocabTrainingData, userId);

const bindHandler = <T>(handler: ((arg: T) => void) | undefined, arg: T) => (handler ? () => handler(arg) : undefined);

const bindAsyncHandler = <T>(handler: ((arg: T) => Promise<void>) | undefined, arg: T) =>
    handler ? () => handler(arg) : undefined;

const bindHandlerWithTwoArgs = <T1, T2>(handler: ((arg1: T1, arg2: T2) => void) | undefined, arg1: T1, arg2: T2) =>
    handler ? () => handler(arg1, arg2) : undefined;

interface MapTestToActivityParams {
    test: TeacherTestDetails;
    handleEditTest: (testId: string, schoolClassId?: string) => Promise<void>;
    handlePrimaryAction?: (testId: string) => void;
    handleDuplicateTest?: (
        testId: string,
        testName: string,
        schoolClassId?: string,
        keepClassByDefault?: boolean
    ) => void;
    handleDeleteTest?: (testId: string, testName: string) => void;
    schoolClassId?: string;
}

export const mapTestToActivity = (params: MapTestToActivityParams): TestActivity => {
    const { test, handleEditTest, handlePrimaryAction, handleDuplicateTest, handleDeleteTest, schoolClassId } = params;
    const testId = test.id ?? "";
    const testName = test.name ?? "";

    return {
        category: ClassActivityCategory.TEST,
        testData: test,
        id: testId,
        sortDate: test.updatedDate || test.createdDate || 0,
        subtitle: testName,
        vocabCount: getVocabCount(test.content),
        bookCover: test.bookInfo?.imagePath
            ? { src: `https://www.phase-6.de${test.bookInfo.imagePath}`, alt: test.bookInfo?.name ?? "" }
            : undefined,
        ownSubjectInfo: test.ownSubjectInfo?.primaryLang
            ? { primaryLang: test.ownSubjectInfo.primaryLang, secondaryLang: test.ownSubjectInfo.secondaryLang }
            : undefined,
        onPrimaryAction: bindHandler(handlePrimaryAction, testId),
        onEdit: handleEditTest ? () => handleEditTest(testId, schoolClassId) : undefined,
        onDuplicate: handleDuplicateTest
            ? () => handleDuplicateTest(testId, testName, test.groupId, !!schoolClassId)
            : undefined,
        onDelete: bindHandlerWithTwoArgs(handleDeleteTest, testId, testName),
    };
};

interface MapVocabTrainingToActivityParams {
    vocabTraining: AeaExerciseModel;
    userUsedBooksInfo: Record<string, GetBandInfoResponse>;
    userUsedOwnSubjectsInfo: Record<string, { name?: string; primaryLang?: string; secondaryLang?: string }>;
    handlePrimaryAction?: (params: OpenShareFlowParams) => void;
    handleOpenDeleteVocabTrainingModal?: (vocabTraining: AeaExerciseModel) => void;
    handleEditVocabTraining?: (vocabTraining: AeaExerciseModel) => Promise<void>;
    handleDuplicateVocabTraining?: (params: DuplicateVocabTrainingPopupState, keepClassByDefault?: boolean) => void;
    schoolClassId?: string;
}

export const mapVocabTrainingToActivity = (params: MapVocabTrainingToActivityParams): VocabTrainingActivity => {
    const {
        vocabTraining,
        userUsedBooksInfo,
        userUsedOwnSubjectsInfo,
        handlePrimaryAction,
        handleOpenDeleteVocabTrainingModal,
        handleEditVocabTraining,
        handleDuplicateVocabTraining,
        schoolClassId,
    } = params;

    const subjectId = vocabTraining.subjectContentId?.id;
    const isOwnContent = subjectId ? !!userUsedOwnSubjectsInfo[subjectId] : false;
    const bookInfo = subjectId && !isOwnContent ? userUsedBooksInfo[subjectId] : undefined;
    const ownSubjectInfo = subjectId && isOwnContent ? userUsedOwnSubjectsInfo[subjectId] : undefined;
    const bookImagePath = bookInfo?.band?.Image;
    const bookOrSubjectName = bookInfo?.band?.ShortName ?? ownSubjectInfo?.name ?? "";
    // Prefer the saved training title (chosen chip / custom title); the book or subject name is
    // only a fallback for legacy trainings saved without one.
    const trainingName = vocabTraining.testName || bookOrSubjectName;

    const vocabularyCount = vocabTraining.cardIds?.length ?? 0;

    return {
        category: ClassActivityCategory.VOCAB_TRAINING,
        vocabTrainingData: vocabTraining,
        bookInfo,
        contentSourceName: bookOrSubjectName,
        id: vocabTraining.id ?? "",
        sortDate: vocabTraining.createdDate ? new Date(vocabTraining.createdDate).getTime() : 0,
        subtitle: trainingName,
        vocabCount: vocabularyCount,
        bookCover:
            !isOwnContent && bookImagePath
                ? { src: `https://www.phase-6.de${bookImagePath}`, alt: bookInfo?.band?.Name ?? "" }
                : undefined,
        ownSubjectInfo: ownSubjectInfo?.primaryLang
            ? {
                  name: ownSubjectInfo.name,
                  primaryLang: ownSubjectInfo.primaryLang,
                  secondaryLang: ownSubjectInfo.secondaryLang,
              }
            : undefined,
        onPrimaryAction: handlePrimaryAction
            ? () => handlePrimaryAction({ training: vocabTraining, bookName: bookOrSubjectName, vocabularyCount })
            : undefined,
        onEdit: bindAsyncHandler(handleEditVocabTraining, vocabTraining),
        onDuplicate: bindHandlerWithTwoArgs(
            handleDuplicateVocabTraining,
            { training: vocabTraining, name: trainingName, bookName: bookOrSubjectName },
            !!schoolClassId
        ),
        onDelete: bindHandler(handleOpenDeleteVocabTrainingModal, vocabTraining),
    };
};
